import { useState } from "react";
import { ChevronDown, Download, Send } from "react-feather";
import dayjs from "dayjs";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-smart-toaster";
import styles from "../MoveOutChecklist.module.css";
import { Badge } from "./CommonComponents";
import { ROOMS } from "./CheckListTab";
import FinalStatementBreakdown, { currency } from "./FinalStatementBreakdown";

// completion.status from the backend (MoveOutCompletion) → badge
const STATUS_BADGES = {
    open: { type: "blue", label: "Open" },
    overdue: { type: "danger", label: "Overdue" },
    partial: { type: "warn", label: "Partial" },
    paid: { type: "success", label: "Paid" },
    due: { type: "blue", label: "Due" },
    received: { type: "success", label: "Received" },
};

// Free-text areas (key counts, notes) have no condition
const TEXT_AREAS = ["keyfobs", "physicalkeys", "mailboxkeys"];

const COND_BADGES = {
    Good: "success",
    Fair: "warn",
    Damaged: "danger",
    "Not inspected": "muted",
};

const roomCondition = (roomData, room) => {
    const values = room.areas
        .filter((area) => area.label !== "Other" && !TEXT_AREAS.includes(area.key))
        .map((area) => roomData?.[area.key])
        .filter((c) => c && c.value);

    if (values.some((c) => c.is_other !== 1 && c.value === "Damaged")) return "Damaged";
    if (values.some((c) => c.is_other === 1)) return "Fair";
    if (values.some((c) => c.value === "New" || c.value === "Normal")) return "Good";
    return "Not inspected";
};

const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY") : "—");

const Section = ({ title, sub, aside, open, onToggle, children }) => (
    <section className={styles.doneAcc}>
        <button className={styles.doneAccHdr} aria-expanded={open} onClick={onToggle}>
            <ChevronDown className={`${styles.doneAccChev} ${open ? styles.doneAccChevOpen : ""}`} />
            <span className={styles.doneAccTitleWrap}>
                <span className={styles.doneAccTitle}>{title}</span>
                {sub && <span className={styles.doneAccSub}>{sub}</span>}
            </span>
            {aside}
        </button>
        {open && <div className={styles.doneAccBody}>{children}</div>}
    </section>
);

const SubmittedTab = ({ leaseData, moveoutData, onClose, setLoading, setSnackbarOpen, setSnackbarMsg }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [open, setOpen] = useState({ pay: true, ins: false, act: false });
    // One section open at a time: opening one closes the others
    const toggle = (key) => setOpen((prev) => ({ pay: false, ins: false, act: false, [key]: !prev[key] }));

    const completion = moveoutData?.completion;
    const statement = completion?.statement;
    const outcome = completion?.outcome || "SETTLED";
    const amount = completion?.amount ?? Math.abs(parseFloat(moveoutData?.final_amount) || 0);
    const isRefund = outcome === "REFUND";
    const isDue = outcome === "BALANCE_DUE";
    const deposit = parseFloat(statement?.security_deposit) || 0;

    const outcomeLabel = isRefund ? "Refund to tenant" : isDue ? "Balance due from tenant" : "Account settled";
    const outcomeNote = isRefund
        ? `Deposit of ${currency(deposit)} held · ${currency(Math.max(0, deposit - amount))} applied to rent and damages`
        : isDue
            ? `Deposit of ${currency(deposit)} fully applied · charges exceed deposit by ${currency(amount)}`
            : `Deposit of ${currency(deposit)} fully covered all move-out charges`;

    const badge = STATUS_BADGES[completion?.status];
    let statusNote = "";
    if (completion?.status === "paid") statusNote = `Paid on ${formatDate(completion?.settled_date)}`;
    else if (completion?.status === "received") statusNote = `Received on ${formatDate(completion?.settled_date)}`;
    else if (completion?.paid_amount > 0) statusNote = `${currency(completion.paid_amount)} ${isRefund ? "paid" : "received"} · ${currency(completion.left_amount)} left`;

    const dates = [
        { label: "Move-out", value: formatDate(moveoutData?.moveout_date) },
        { label: "Inspection", value: formatDate(moveoutData?.inspection_date) },
        { label: "Statement sent", value: formatDate(completion?.sent_at) },
    ];
    if (isRefund || isDue) {
        dates.push({ label: isRefund ? "Refund deadline" : "Payment due", value: formatDate(completion?.due_date), badge, note: statusNote });
    }

    const rooms = ROOMS.map((room) => ({ name: room.name, cond: roomCondition(moveoutData?.[room.key], room) }));
    const condCounts = ["Good", "Fair", "Damaged"]
        .map((c) => ({ c, n: rooms.filter((r) => r.cond === c).length }))
        .filter((x) => x.n > 0)
        .map((x) => `${x.n} ${x.c}`)
        .join(" · ");

    const sendReport = async () => {
        setLoading && setLoading(true);
        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
            const response = await axios.post(process.env.REACT_APP_PAYMENT_API_URL + "v1/moveout-report/" + moveoutData?.tenant_id + "/resend", {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
        } catch (error) {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
        }
        setLoading && setLoading(false);
    };

    return <div className={`${styles.tp} ${styles.tpActive}`}>
        <section aria-label="Settlement outcome" className={`${styles.doneOutcome} ${isDue ? styles.doneOutcomeDue : styles.doneOutcomeCredit}`}>
            <span className={styles.doneLab}>{outcomeLabel}</span>
            <span className={styles.doneAmount}>{currency(amount)}</span>
            {statement && <span className={styles.doneNote}>{outcomeNote}</span>}
        </section>

        <div className={styles.doneDates}>
            {dates.map((d) => (
                <div className={styles.doneDate} key={d.label}>
                    <span className={styles.doneLab}>{d.label}</span>
                    <div className={styles.doneDateValue}>
                        <span>{d.value}</span>
                        {d.badge && <Badge type={d.badge.type}>{d.badge.label}</Badge>}
                    </div>
                    {d.note && <span className={styles.doneDateNote}>{d.note}</span>}
                </div>
            ))}
        </div>

        <Section
            title="Payment details"
            sub="Previous balance · Final month rent · Security deposit · Damage charges · Voided bills"
            aside={<span className={`${styles.doneAccAmount} ${isDue ? styles.charge : styles.credit}`}>{currency(amount)}</span>}
            open={open.pay}
            onToggle={() => toggle("pay")}
        >
            {statement
                ? <FinalStatementBreakdown statement={statement} moveoutDate={moveoutData?.moveout_date} />
                : <p className={styles.doneEmpty}>This move-out was submitted before statement details were saved. Download the Move-Out Report for the final statement.</p>}
        </Section>

        <Section
            title="Inspection checklist"
            sub={`Inspected ${formatDate(moveoutData?.inspection_date)} · ${rooms.length} areas${moveoutData?.signed_by_name ? ` · Signed by ${moveoutData.signed_by_name}` : ""}`}
            aside={<span className={styles.doneAccMeta}>{condCounts}</span>}
            open={open.ins}
            onToggle={() => toggle("ins")}
        >
            <div className={styles.doneRooms}>
                {rooms.map((room) => (
                    <div className={styles.sr} key={room.name}>
                        <span>{room.name}</span>
                        <Badge type={COND_BADGES[room.cond]}>{room.cond}</Badge>
                    </div>
                ))}
            </div>
        </Section>

        <Section
            title="Activity"
            sub="Audit trail for this move-out"
            open={open.act}
            onToggle={() => toggle("act")}
        >
            <ol className={styles.doneActivity}>
                {(completion?.activity || []).map((a, i) => (
                    <li key={i}>
                        <span className={styles.doneActivityDot} />
                        <span className={styles.doneActivityWhat}>{a.what}</span>
                        <span className={styles.doneActivityWhen}>{a.when}</span>
                    </li>
                ))}
            </ol>
        </Section>

        <div className={styles.fa}>
            <button className={`${styles.btn} ${styles.btnS}`} onClick={onClose}>Close</button>
            <div className={styles.faRight}>
                <button className={`${styles.btn} ${styles.btnS}`} onClick={sendReport}>
                    <Send width={16} height={16} /> Send Move-Out Report
                </button>
                <button className={`${styles.btn} ${styles.btnP}`} onClick={() => window.open(moveoutData?.report_file_url, "_blank")} disabled={!moveoutData?.report_file_url}>
                    <Download width={16} height={16} /> Download Move-Out Report
                </button>
            </div>
        </div>
    </div>
}

export default SubmittedTab;
